import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { colors } from "../config/colors";
import { FONT_SIZE_MD, FONT_WEIGHT_NORMAL, LINE_HEIGHT_SM } from "../config/styles";

// Status type constants
export const STATUS_TYPES = {
  // Plan Status
  ACTIVE: "Active",
  CANCELLED: "Cancelled",
  COMPLETED: "Completed",
  
  // Payment Status
  SUCCESS: "Success",
  FAILED: "Failed",
  PENDING: "Pending",
  PAID: "Paid",
  EXPIRED: "Expired",
};

// Map backend status codes to display labels for Pay by Link
export const STATUS_LABEL_MAP = {
  "UNUSED_OTT": "Active",
  "INPROGRESS": "In Progress",
  "EXPIRED_OTT": "Expired",
  "CANCELLED": "Cancelled",
  "PROCESSED": "Processed",
};

// Helper function to get display label from backend status code
export const getStatusDisplayLabel = (status) => {
  if (!status) return "";
  return STATUS_LABEL_MAP[status] || status;
};

// Color mapping for different status types
// @param {string} status - The status text
// @param {string} [activeColor] - Optional override color for "active" status (e.g. green for Direct Debit)
export const getStatusColor = (status, activeColor) => {
  if (!status) return colors.custom;
  
  const statusLower = status.toLowerCase();
  
  // Paused states (orange)
  if (statusLower === "paused") {
    return colors.statusPending;
  }

  // Success/Paid states (green)
  if (statusLower === "success" || statusLower === "completed" || statusLower === "paid") {
    return colors.statusSuccess;
  }
  
  // Failed states (red)
  if (statusLower === "failed") {
    return colors.warning;
  }
  
  // Cancelled states (gray color)
  if (statusLower === "cancelled") {
    return "#858C9A";
  }
  
  // Expired states (gray/muted)
  if (statusLower === "expired" || statusLower === "expired_ott") {
    return "#858C9A";
  }
  
  // Pending/Active states (orange)
  if (statusLower === "pending" || statusLower === "unused_ott" || statusLower === "active") {
    return colors.statusPending;
  }
  
  // In Progress states (active processing)
  if (statusLower === "inprogress" || statusLower === "in progress") {
    return colors.textPrimary;
  }
  
  // Processed/Paid states (green)
  if (statusLower === "processed") {
    return colors.statusSuccess;
  }

  // Default
  return colors.custom;
};

const StyledStatusBadge = styled("span")(({ statusColor }) => ({
  color: statusColor,
  fontSize: FONT_SIZE_MD,
  fontWeight: FONT_WEIGHT_NORMAL,
  lineHeight: LINE_HEIGHT_SM,
  fontFamily: "Open Sans, sans-serif",
}));

/**
 * StatusBadge Component
 * 
 * Displays a status with appropriate color based on status type.
 * Used across Smart Plans, Direct Debit, and other screens.
 * 
 * @param {string} status - The status text to display
 * @param {string} [customColor] - Override color for ALL statuses (highest priority)
 * @param {string} [activeColor] - Override color for "active" status only (e.g. green for Direct Debit)
 */
export const StatusBadge = ({ status, customColor, activeColor }) => {
  const statusColor = customColor || getStatusColor(status, activeColor);
  const displayLabel = getStatusDisplayLabel(status);
  
  return (
    <StyledStatusBadge statusColor={statusColor}>
      {displayLabel}
    </StyledStatusBadge>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string,
  customColor: PropTypes.string,
  activeColor: PropTypes.string,
};

StatusBadge.defaultProps = {
  status: "",
  customColor: null,
  activeColor: null,
};